import { Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@rudi/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@rudi/ui/dialog';
import { useDeleteSignupAttempt } from '../api/hooks';
import type { SignupAttempt } from '../api/types';

function errorMessage(error: unknown): string {
  const response = (
    error as { response?: { status?: number; data?: { detail?: string; error?: string } } }
  )?.response;
  // Rudi from before sign-up attempt deletion answers 404 for the route itself.
  if (response?.status === 404 && !response.data?.detail)
    return 'This Rudi cannot delete sign-up attempts yet. Mark it reviewed instead.';
  return (
    response?.data?.detail ??
    response?.data?.error ??
    (error instanceof Error ? error.message : 'Delete failed')
  );
}

/**
 * Delete a refused sign-up: every attempt of its email goes, and so does the
 * account a refused sign-in left behind, unless an invitation waits for the
 * email or the person has joined a tenant since.
 */
export function DeleteSignupAttemptDialog({
  attempt,
  onClose,
}: {
  attempt: SignupAttempt | null;
  onClose: () => void;
}) {
  const del = useDeleteSignupAttempt();

  const onDelete = async () => {
    if (!attempt || del.isPending) return;
    try {
      const result = await del.mutateAsync(attempt.id);
      toast.success(
        result.account_deleted
          ? `${attempt.email} deleted, with the account the sign-up left behind`
          : `${attempt.email} deleted`
      );
      onClose();
    } catch (error) {
      toast.error(errorMessage(error));
    }
  };

  return (
    <Dialog open={attempt !== null} onOpenChange={next => !next && !del.isPending && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trash2 className="h-5 w-5 text-destructive" />
            Delete sign-up attempt
          </DialogTitle>
          <DialogDescription>
            {attempt
              ? `Every sign-up attempt of ${attempt.email} is deleted. If they signed in, the account Rudi refused is deleted too, unless an invitation waits for this email or they have joined a tenant since. A later invitation then starts from a clean slate.`
              : ''}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={del.isPending}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={() => void onDelete()} disabled={del.isPending}>
            {del.isPending ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
